<template>
  <Layout>
    <PageHeader :title="title" :subTitle="subTitle">
      <template #afterTitle>
        <McButton
          v-if="canCreateTemplate"
          :data-me="`${dataMePrefix}-btn-create-template`"
          variant="primary"
          icon="icon-fill_plus"
          :text="buttonLabels.createTemplate"
          @click="handleCreateTemplate"
        />
      </template>
    </PageHeader>

    <McGrapqlTable
      withCard
      searchable
      stickyHeader
      :fields="fields"
      :apiClient="apiClient"
      :hasCheckbox="false"
      :hasDelete="false"
      :hasFilter="true"
      :isSelectableField="false"
      :activeFilters="activeFilters"
      :customHandlers="customHandlers"
    >
      <template #button-filter>
        <div class="vehicle-inspection-list__toolbar">
          <McInlineFilter
            :data-me="`${dataMePrefix}-filter-result`"
            :filterItems="inlineFilterItems"
            :activeKeys="activeResultKeys"
            @update:activeKeys="handleResultFilter"
          />

          <McTableFilter
            :buttonProps="{ 'data-me': `${dataMePrefix}-btn-filter` }"
            :activeFilters="activeFilters"
            :parseFilterDataMappings="filterMappings"
            @submit="handleChangeActiveFilters"
          >
            <template
              #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }"
            >
              <InputTableFilter
                :formValues="formValues"
                :fullObjects="fullObjects"
                @update:formValues="updateFormValues"
                @update:fullObjects="updateFullObjects"
              />
            </template>
          </McTableFilter>
        </div>
      </template>

      <template #cell(documentNumber)="{ value, item }">
        <button
          type="button"
          class="vehicle-inspection-list__detail-link fw-bold text-primary td__noellipsis clickable-field"
          :data-me="`${dataMePrefix}-link-detail-document-${item.inspectionId}`"
          @click="handleClickRow(item)"
        >
          {{ value }}
        </button>
      </template>

      <template #cell(submittedAt)="{ value }">
        <div>{{ formatDate(value).date }}</div>
        <div class="mc-datetime">
          {{ formatDate(value).time }}
        </div>
      </template>

      <template #cell(healthyState)="{ value, item }">
        <div class="vehicle-inspection-list__result">
          <McBadge
            :text="resultLabel(value)"
            :variant="value ? 'success' : 'danger'"
            type="status"
          />
          <span v-if="!value" class="text-danger">
            ({{ item.stats.unhealthy }}/{{ item.stats.totalQuestion }})
          </span>
        </div>
      </template>
    </McGrapqlTable>
  </Layout>
</template>

<script>
import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2'
import McButton from '@/components/McButton'
import McGrapqlTable from '@/components/McGrapqlTable'
import McInlineFilter from '@/components/McInlineFilter'
import McTableFilter from '@/components/McTableFilter'
import McBadge from '@/components/McBadge'
import { GET_VEHICLES_INSPECTION } from '@/services/api/vehicleInspection'
import InputTableFilter from './components/InputTableFilter.vue'
import {
  buildVehicleInspectionsInput,
  getVehicleInspectionTemplateRoute,
  normalizeVehicleInspectionsResponse,
} from './lib/vehicleInspectionAdapter'
import { mapGetters } from 'vuex'

export default {
  components: {
    Layout,
    PageHeader,
    McButton,
    McGrapqlTable,
    McInlineFilter,
    McTableFilter,
    McBadge,
    InputTableFilter,
  },
  data() {
    return {
      dataMePrefix: 'vehicle-inspection-list',
      activeFilters: {},
      filterMappings: {
        vehicles: 'vehicleIds',
        drivers: 'inspectorIds',
        submitted_period: ['submittedAtStart', 'submittedAtEnd'],
        result: 'isHealthy',
      },
      customHandlers: {
        handleClickRow: this.handleClickRow,
      },
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    canCreateTemplate() {
      return this.hasAccess(['c19a-default-vehicle-inspection:create'])
    },
    canUpdateTemplate() {
      return this.hasAccess(['c19a-default-vehicle-inspection:update'])
    },
    title() {
      return this.$t('vehicleInspection.list.title')
    },
    subTitle() {
      return this.$t('vehicleInspection.list.subtitle')
    },
    buttonLabels() {
      return { createTemplate: this.$t('vehicleInspection.list.createTemplate') }
    },
    fields() {
      return [
        {
          key: 'documentNumber',
          sortKey: 'code',
          label: this.$t('vehicleInspection.field.documentNumber'),
          isSortable: true,
          isRedirectable: true,
        },
        {
          key: 'templateName',
          label: this.$t('vehicleInspection.field.templateName'),
          isSortable: true,
        },
        {
          key: 'submittedLicensePlate',
          sortKey: 'licensePlate',
          label: this.$t('vehicleInspection.field.licensePlate'),
          isSortable: true,
        },
        {
          key: 'inspectorName',
          label: this.$t('vehicleInspection.field.driver'),
          isSortable: true,
        },
        {
          key: 'submittedAt',
          label: this.$t('vehicleInspection.field.submittedAt'),
          isSortable: true,
        },
        {
          key: 'healthyState',
          label: this.$t('vehicleInspection.field.result'),
          isSortable: false,
        },
      ]
    },
    inlineFilterItems() {
      return [
        { id: 'all', label: this.$t('vehicleInspection.status.all') },
        { id: 'healthy', label: this.$t('vehicleInspection.status.eligible') },
        { id: 'unhealthy', label: this.$t('vehicleInspection.status.notEligible') },
      ]
    },
    activeResultKeys() {
      if (this.activeFilters.isHealthy === true) return ['healthy']
      if (this.activeFilters.isHealthy === false) return ['unhealthy']
      return []
    },
    apiClient() {
      return {
        get: this.getInspections,
      }
    },
  },
  methods: {
    resultLabel(healthyState) {
      return healthyState
        ? this.$t('vehicleInspection.status.eligible')
        : this.$t('vehicleInspection.status.notEligible')
    },
    async getInspections(filter = {}) {
      try {
        const input = buildVehicleInspectionsInput({
          tableFilter: filter,
          activeFilters: this.activeFilters,
        })
        const response = await this.$apollo.query({
          query: GET_VEHICLES_INSPECTION,
          fetchPolicy: 'no-cache',
          variables: { filter: input },
        })
        const result = response.data.vehicleInspections
        if (!result?.isSuccessful) {
          throw new Error(result?.message || this.$t('global.somethingWentWrong'))
        }

        return normalizeVehicleInspectionsResponse(response.data)
      } catch (error) {
        this.$mcToast.error(error.message || this.$t('global.somethingWentWrong'))
        return {
          data: [],
          pagination: {
            page: filter.pagination?.page ?? 1,
            show: filter.pagination?.show ?? 10,
            total: 0,
          },
        }
      }
    },
    formatDate(value) {
      const date = new Date(value)

      const locale = this.$i18n.locale === 'en' ? 'en-US' : 'id-ID'

      return {
        date: new Intl.DateTimeFormat(locale, {
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }).format(date),
        time: `${new Intl.DateTimeFormat(locale, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
        }).format(date)} WIB`,
      }
    },
    handleResultFilter(resultKeys) {
      const nextFilters = { ...this.activeFilters }
      delete nextFilters.isHealthy
      if (resultKeys[0] === 'healthy') nextFilters.isHealthy = true
      if (resultKeys[0] === 'unhealthy') nextFilters.isHealthy = false
      this.activeFilters = nextFilters
    },
    handleChangeActiveFilters(filters) {
      const nextFilters = { ...this.activeFilters }

      filters.forEach(({ key, values }) => {
        delete nextFilters[key]
        if (values !== null && values !== undefined && values !== '') {
          nextFilters[key] = values
        }
      })
      this.activeFilters = nextFilters
    },
    handleClickRow(item) {
      this.$router.push({
        name: 'Vehicle Inspection Form',
        params: { id: item.inspectionId },
        query: item.healthyState === false ? { condition: 'unhealthy' } : undefined,
      })
    },
    handleClickTemplate(item) {
      this.$router.push(
        getVehicleInspectionTemplateRoute(item, { canUpdate: this.canUpdateTemplate }),
      )
    },
    handleCreateTemplate() {
      this.$router.push({ name: 'Create Setting Template Vehicle Inspection' })
    },
  },
}
</script>

<style lang="scss" scoped>
.vehicle-inspection-list {
  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 12px;
  }

  &__detail-link {
    padding: 0;
    border: 0;
    background: transparent;
    color: $primary;
    text-align: left;

    &:hover {
      text-decoration: underline;
    }
  }

  &__result {
    display: flex;
    align-items: center;
    gap: 8px;
    justify-content: space-between;
  }

}

@media (max-width: 767.98px) {
  .vehicle-inspection-list__toolbar {
    align-items: stretch;
    flex-direction: column;
  }
}
</style>
